import { useParams } from 'react-router-dom'

import { RelativeTime, UserAvatar } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { useT, type I18n } from '@/shared/i18n'
import type { ActivityEntry } from '@/shared/types/domain'

import { useProjectActivity } from '../api'
import { ActivityDiff } from './ActivityDiff'

/**
 * LA FRASE DE LA ACTIVIDAD ES NUESTRA, NO DEL SERVIDOR.
 *
 * El log de auditoría devuelve `action` (`project.stage_changed`,
 * `task.completed`…) y nada más legible: el `summary` que trae `ActivityEntry`
 * lo arma el adaptador del front pegando un verbo con el tipo de entidad, que
 * es exactamente la concatenación que se rompe al traducir. Así que la frase se
 * elige acá del catálogo, entera y por acción, y ese `summary` armado no se
 * usa.
 *
 * Una acción sin punto (`created`) es sobre el proyecto mismo: la consulta
 * filtra `entity_type=project`. Por eso se busca también como `project.<acción>`.
 */
const ACTION_KEY: Record<string, string> = {
  'project.created': 'projects.activity.action.projectCreated',
  'project.updated': 'projects.activity.action.projectUpdated',
  'project.deleted': 'projects.activity.action.projectDeleted',
  'project.restored': 'projects.activity.action.projectRestored',
  'project.moved': 'projects.activity.action.projectMoved',
  'project.stage_changed': 'projects.activity.action.projectStageChanged',
  'project.visibility_changed': 'projects.activity.action.projectVisibilityChanged',
  'task.created': 'projects.activity.action.taskCreated',
  'task.updated': 'projects.activity.action.taskUpdated',
  'task.completed': 'projects.activity.action.taskCompleted',
  'document.created': 'projects.activity.action.documentCreated',
  'document.updated': 'projects.activity.action.documentUpdated',
}

/** Una acción que todavía no tiene frase propia se cuenta con su código adentro. */
function actionPhrase(t: I18n['t'], entry: ActivityEntry): string {
  const key = ACTION_KEY[entry.action] ?? ACTION_KEY[`project.${entry.action}`]
  return key ? t(key) : t('projects.activity.action.other', { action: entry.action })
}

export function ProjectActivityTab() {
  const { projectId = '' } = useParams()
  const t = useT()
  const query = useProjectActivity(projectId)

  if (query.isPending) return <SkeletonRows rows={5} columns={[0.25, 0.5]} withSpine={false} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const entries = query.data ?? []
  if (entries.length === 0) {
    return (
      <EmptyState
        title={t('projects.activity.emptyTitle')}
        description={t('projects.activity.emptyDescription')}
      />
    )
  }

  return (
    <ol className="flex flex-col gap-2">
      {entries.map((entry) => (
        <li
          key={entry.id}
          className="hairline flex flex-col gap-1 rounded-[var(--radius-card)] bg-surface-1 px-3 py-2"
        >
          <span className="flex items-center gap-2">
            <UserAvatar user={entry.actor} />
            <span className="min-w-0 flex-1 truncate text-xs text-text-2">
              {/* El nombre de quien actuó viaja tal cual; la frase sale del catálogo. */}
              <span className="text-text-1">
                {entry.actor?.display_name ?? t('projects.activity.system')}
              </span>{' '}
              {actionPhrase(t, entry)}
            </span>
            {/* El código de la acción es un identificador técnico, no prosa. */}
            <span className="num text-2xs text-text-3">{entry.action}</span>
            <RelativeTime value={entry.at} />
          </span>

          {/*
            DE QUÉ A QUÉ. Es la mitad de la pregunta que esta pantalla no
            contestaba: "quién lo movió" sin "desde dónde" obliga a abrir el
            proyecto igual. Va vacío en altas y borrados, donde no hay diff.
          */}
          <ActivityDiff changes={entry.changes} />
        </li>
      ))}
    </ol>
  )
}
